import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageHeader from '~/components/Common/Elements/PageHeader';
import csAgents from '~/components/Common/csAgents.json';
import type { CsAgent } from '~/components/SmsVerifyStats/smsVerifyStatsUtils';
import {
  calculateMaxCount,
  formatDateString,
  getCountColor,
  getFilteredEmailsForChart,
  getSubordinateEmails,
} from '~/components/SmsVerifyStats/smsVerifyStatsUtils';

import { useAiSummaryStats } from '~/hooks/apis/reporting/useAiSummaryStats';
import useAppNavigation from '~/hooks/useAppNavigation';
import useUrlQuery from '~/hooks/useQuery';

import AiSummaryStatsCsvExport from './AiSummaryStatsCsvExport';
import { transformAiSummaryStats, transformAiSummaryStatsForTable } from './transformAiSummaryStats';

export default function AiSummaryStatsPage() {
  const theme = useTheme();
  const location = useLocation();
  const { replace } = useAppNavigation();
  const urlQuery = useUrlQuery();
  const selectedTeamLeader = urlQuery.get('teamLeader') || '';

  function setSelectedTeamLeader(teamLeader: string) {
    const urlParams = new URLSearchParams(location.search);
    if (teamLeader) {
      urlParams.set('teamLeader', teamLeader);
    } else {
      urlParams.delete('teamLeader');
    }

    const newSearch = urlParams.toString();
    replace(
      {
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      },
      location.state,
    );
  }

  const { data: aiSummaryStatsData, isLoading, error } = useAiSummaryStats();

  const filteredEmailsForChart = useMemo(() => {
    return getFilteredEmailsForChart(selectedTeamLeader, csAgents as Array<CsAgent>);
  }, [selectedTeamLeader]);

  const chartData = useMemo(() => {
    if (!aiSummaryStatsData) {
      return [];
    }

    return transformAiSummaryStats(aiSummaryStatsData, filteredEmailsForChart);
  }, [aiSummaryStatsData, filteredEmailsForChart]);

  const allTableData = useMemo(() => {
    if (!aiSummaryStatsData) {
      return [];
    }

    return transformAiSummaryStatsForTable(aiSummaryStatsData, csAgents as Array<CsAgent>);
  }, [aiSummaryStatsData]);

  const teamLeaders = useMemo(() => {
    return (csAgents as Array<CsAgent>).map((agent) => ({
      email: agent.Email,
      name: agent.Name,
    }));
  }, []);

  const tableData = useMemo(() => {
    if (!selectedTeamLeader) {
      return allTableData;
    }

    const subordinateEmails = getSubordinateEmails(selectedTeamLeader, csAgents as Array<CsAgent>);
    return allTableData.filter((row) => subordinateEmails.has(row.email.toLowerCase()));
  }, [allTableData, selectedTeamLeader]);

  const dayHeaders = useMemo(() => {
    return tableData[0]?.dayCounts.map((dayCount) => formatDateString(dayCount.date)) || [];
  }, [tableData]);

  const dateRangeDays = useMemo(() => {
    return allTableData[0]?.dayCounts.length || 0;
  }, [allTableData]);

  const maxCount = useMemo(() => {
    return calculateMaxCount(tableData);
  }, [tableData]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>AI Call Summary usage | Admin</title>
      </Helmet>

      <PageHeader title="AI Call Summary usage" />

      <Box mt={4}>
        <Alert severity="info" variant="outlined">
          <Typography variant="body1">
            This dashboard tracks daily opens of the Zendesk AI Summary app to help support leaders measure adoption
            across teams.
          </Typography>
        </Alert>
      </Box>

      {error && (
        <Box mt={4}>
          <Alert severity="error">
            Error loading AI summary stats: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {isLoading && (
        <Box mt={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && !error && aiSummaryStatsData && (
        <Box mt={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {selectedTeamLeader
                ? `Daily AI Call Summary Opens - ${
                    teamLeaders.find((leader) => leader.email === selectedTeamLeader)?.name || 'Selected Team'
                  }`
                : 'Daily AI Call Summary Opens - total across all agents'}
            </Typography>
            <Box mt={3}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDateString} />
                  <YAxis />
                  <Tooltip labelFormatter={formatDateString} />
                  <Line type="monotone" dataKey="totalCount" stroke={theme.palette.primary.main} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      )}

      {!isLoading && !error && aiSummaryStatsData && (
        <Box mt={4}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6">
                {dateRangeDays > 0
                  ? `Agent Daily Counts (Last ${dateRangeDays} ${dateRangeDays === 1 ? 'Day' : 'Days'})`
                  : 'Agent Daily Counts'}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <AiSummaryStatsCsvExport tableData={tableData} />
                <FormControl sx={{ minWidth: 250 }}>
                  <InputLabel id="team-leader-select-label">Filter by Team Leader</InputLabel>
                  <Select
                    labelId="team-leader-select-label"
                    id="team-leader-select"
                    value={selectedTeamLeader}
                    label="Filter by Team Leader"
                    onChange={(event) => setSelectedTeamLeader(event.target.value)}
                  >
                    <MenuItem value="">
                      <Typography fontStyle="italic">All Agents</Typography>
                    </MenuItem>
                    {teamLeaders.map((leader) => (
                      <MenuItem key={leader.email} value={leader.email}>
                        {leader.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Email
                      </Typography>
                    </TableCell>
                    {dayHeaders.map((day) => (
                      <TableCell key={day} align="center">
                        <Typography variant="subtitle2" fontWeight="bold">
                          {day}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <Typography variant="subtitle2" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row) => (
                    <TableRow key={row.email}>
                      <TableCell>
                        <Typography variant="body2">{row.email}</Typography>
                      </TableCell>
                      {row.dayCounts.map((dayCount) => (
                        <TableCell
                          key={`${row.email}-${dayCount.date}`}
                          align="center"
                          sx={{
                            backgroundColor: getCountColor(dayCount.count, maxCount),
                          }}
                        >
                          <Typography variant="body2">{dayCount.count}</Typography>
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          {row.total}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
    </Container>
  );
}
